import { h, defineComponent, ref, watch } from 'vue';
import { hasFeature, hasPermission, Permissions, user as authUser } from '@/ui/auth/auth';
import { renderAnalyticsTeaserPage } from '@/ui/pages/teasers/AnalyticsTeaserPage';
import { renderForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
import MostOverdueInspectionsTableCard from '@/_legacy/views/Analytics/Overview/MostOverdueInspectionsTableCard.vue';
import { InspectionGroupBy } from '@/services/api/resources/analytics/types';
import {
  groupedInspectionList,
  groupedInspectionListExportUrl,
  SupportedExportFormat,
} from '@/services/api/resources/analytics';
import { handleOverdueInspectionClick, redirectOverdueInspections } from '@/ui/pages/analytics/Overview/redirects';
import { useTask } from '@/ui/concerns';
import GroupTitleDecoder from '@/_legacy/views/Analytics/decoders/GroupTitleDecoder';
import { download } from '@/ui/helpers/uiHelper';
import { useQueries } from '@/ui/pages/analytics/Overview/useQueries';
import { parseInt10 } from '@/ui/helpers';

export const MostOverdueInspectionsCard = defineComponent({
  name: 'MostOverdueInspectionsCard',
  setup() {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);

    const groupMostOverdueInspections = ref<InspectionGroupBy>(InspectionGroupBy.Assignee);

    const {
      mostOverdueInspectionsQuery,
    } = useQueries();

    const tableList = ref<unknown>([]);

    const getList = useTask(
      () => {
        initialLoading.value = true;

        return groupedInspectionList(groupMostOverdueInspections.value, mostOverdueInspectionsQuery());
      },
      tableResponse => {
        tableList.value = tableResponse.data.map(result => ({
          label: GroupTitleDecoder(result.group),
          group: result.group,
          value: result.overdueInspectionCount,
        }));

        initialLoading.value = false;
      },
    );
    // export task
    const { run: exportData } = useTask(
      (format: SupportedExportFormat | undefined) => groupedInspectionListExportUrl(
        groupMostOverdueInspections.value,
        { ...mostOverdueInspectionsQuery(), perPage: parseInt10(process.env.VUE_APP_MAX_ANALYTICS_EXPORT!) },
        format as SupportedExportFormat,
        authUser.value.locale,
      ),
      download,
    );

    watch([
      () => mostOverdueInspectionsQuery(),
      groupMostOverdueInspections,
    ], getList.run, { deep: true });

    getList.run();

    return function render() {
      //legacy, not compatible with Vue typings
      //@ts-ignore
      return h(MostOverdueInspectionsTableCard, {
        props: {
          results: tableList.value,
          loading: initialLoading.value,
          groupBy: groupMostOverdueInspections.value,
        },
        on: {
          click: () => redirectOverdueInspections(mostOverdueInspectionsQuery()),
          groupByChanged: (value: InspectionGroupBy) => {
            groupMostOverdueInspections.value = value;
          },
          exportClicked: (format: SupportedExportFormat) => exportData(format),
          rowClick: (
            { row, groupBy }: { row: { group: { id: number } }; groupBy: InspectionGroupBy },
          ) => handleOverdueInspectionClick(mostOverdueInspectionsQuery(), row, groupBy),
        },
      });
    };
  },
});
